import React, { useRef } from "react";

import * as Sentry from "@sentry/react";
import { AddressElement, AddressElementProps } from "@stripe/react-stripe-js";
import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { isEmpty, trim } from "lodash/fp";

import StripeForm from "@app/analytics/account/Billing/components/StripeForm";
import StripeFormField from "@app/analytics/account/Billing/components/StripeFormField";
import { showToast } from "@app/common/hooks/useToasts";
import { CheckoutBillingInfo } from "@app/common/types";

import Button from "@rui/Button";
import Stack from "@rui/Stack";

interface AddressFormProps {
  checkoutBillingInfo: CheckoutBillingInfo;
  onLoadError: AddressElementProps["onLoadError"];
  onSave: (checkoutBillingInfo: CheckoutBillingInfo) => void;
  loading?: boolean;
}

interface StripeAddressValues {
  name: string;
  address?: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

const getStripeFormValues = (
  billingInfo: CheckoutBillingInfo
): StripeAddressValues => {
  return {
    name: billingInfo.name ?? "",
    address: billingInfo.address
      ? {
          line1: billingInfo.address.line1,
          line2: billingInfo.address.line2,
          city: billingInfo.address.city,
          state: billingInfo.address.state,
          postal_code: billingInfo.address.postalCode,
          country: billingInfo.address.country,
        }
      : undefined,
  };
};

const getBillingInfoFromStripeFormValues = (
  currentBillingInfo: CheckoutBillingInfo,
  stripeValues: StripeAddressValues,
  company?: string
): CheckoutBillingInfo => {
  if (!stripeValues.address) {
    throw new Error("Stripe address values are missing");
  }

  return {
    ...currentBillingInfo,
    company: isEmpty(company) ? undefined : company,
    name: stripeValues.name,
    address: {
      line1: stripeValues.address.line1,
      line2: stripeValues.address.line2,
      city: stripeValues.address.city,
      state: stripeValues.address.state,
      postalCode: stripeValues.address.postal_code,
      country: stripeValues.address.country,
    },
  };
};

const AddressForm = ({
  checkoutBillingInfo,
  onLoadError,
  onSave,
  loading,
}: AddressFormProps) => {
  const companyRef = useRef<HTMLInputElement | null>(null);
  const [addressValues, setAddressValues] =
    React.useState<StripeAddressValues | null>();

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    setAddressValues(event.complete ? event.value : null);
  };

  const handleSave = () => {
    if (!addressValues) {
      showToast({ severity: "error", message: "Please enter an address" });
      Sentry.captureMessage("Billing address form submitted without address");
      return;
    }

    const company = companyRef.current?.value
      ? trim(companyRef.current.value)
      : undefined;

    onSave(
      getBillingInfoFromStripeFormValues(
        checkoutBillingInfo,
        addressValues,
        company
      )
    );
  };

  return (
    <Stack spacing={2}>
      <StripeForm>
        <StripeFormField
          label="Company (optional)"
          name="company"
          placeholder="Company Name"
          inputProps={{ maxLength: 30 }}
          ref={companyRef}
          defaultValue={checkoutBillingInfo.company}
        />
        <AddressElement
          id="stripe-address-element"
          options={{
            mode: "billing",
            defaultValues: getStripeFormValues(checkoutBillingInfo),
          }}
          onChange={handleAddressChange}
          onLoadError={onLoadError}
        />
      </StripeForm>
      <Button
        data-test-id="billing-submit-billing-info"
        disabled={!addressValues}
        loading={loading}
        onClick={handleSave}
      >
        Continue
      </Button>
    </Stack>
  );
};

export default AddressForm;
